
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../lib/firebase';
import { AppNotification } from '../types';
import { Toast } from '../components/ui/Toast';

interface NotificationContextType {
  notifications: AppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>({
  notifications: [],
  unreadCount: 0,
  markAsRead: async () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<AppNotification[]>([]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return;
    }

    const query = db.collection('notifications')
      .where('userId', '==', user.uid)
      .limit(50);

    const unsubscribe = query.onSnapshot((snapshot) => {
      const msgs = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() } as AppNotification))
        .sort((a, b) => b.createdAt - a.createdAt);

      setNotifications(msgs);
      
      snapshot.docChanges().forEach((change) => {
        if (change.type === 'added') {
          const newMsg = { id: change.doc.id, ...change.doc.data() } as AppNotification;
          if (newMsg.createdAt > Date.now() - 10000 && !newMsg.read) {
             setToasts(prev => [...prev, newMsg]);
          }
        }
      });
    });

    return () => unsubscribe();
  }, [user]);

  const markAsRead = async (id: string) => {
    try {
      await db.collection('notifications').doc(id).delete();
    } catch (e) {
      console.error("Failed to mark read/delete", e);
    }
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    markAsRead(id);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{ notifications, unreadCount, markAsRead }}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3">
        {toasts.map(toast => (
          <Toast key={toast.id} notification={toast} onClose={removeToast} />
        ))}
      </div>
    </NotificationContext.Provider>
  );
};
